<script setup>
  import {computed, onMounted, onUnmounted, ref} from "vue";

  import ComponentFoldable from "@components/Foldable/Foldable.vue";
  import {usePage} from "@inertiajs/vue3";
  import PatientinputClientComponentShareDialog from "@pages/PatientinputClient/Components/ShareDialog.vue";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentPatientinputClientSidebar",
  });

  const props = defineProps({
    open: {
      type: Boolean,
      default: true,
    },
    dynamicWidth: {
      type: Boolean,
      default: true,
    },
  });

  const pageProperties = usePage().props;

  const refInfoDialog = ref(null);

  const hidden = ref(false); // always visible by default (initially)

  const clientCompany = computed(() => pageProperties.clientCompany);

  const handleResizeBreakpoint = (event) => {
    if (props.dynamicWidth === false) {
      // early exit when prop disables dynamic width of the sidebar
      return;
    }

    // otherwise we always "prefer" the sidebar to be open (initially)
    // and hide it, when the display is smaller than xl
    if (event.detail.breakpoint === "xl" || event.detail.breakpoint === "2xl") {
      hidden.value = false;
    } else {
      hidden.value = true;
    }
  };

  onMounted(() => {
    globalThis.addEventListener("breakpoint", handleResizeBreakpoint);
  });

  onUnmounted(() => {
    globalThis.removeEventListener("breakpoint", handleResizeBreakpoint);
  });

  const toggleHidden = () => {
    // when the user has changed the hidden status manually
    // we do not listen on breakpoint events (window
    // resize events) anymore.
    globalThis.removeEventListener("breakpoint", handleResizeBreakpoint);

    hidden.value = !hidden.value;
  };

  const openPatientInputDialog = () => {
    refInfoDialog.value.open(false);
  };
  const openCarerInputDialog = () => {
    refInfoDialog.value.open(true);
  };
</script>

<template>
  <aside
    ref="refPatientinputSidebar"
    class="relative bg-white px-4 shadow-md transition-all"
    :class="{'-ml-4 w-12 pt-12 pb-4': hidden, 'w-64 pt-4': !hidden}"
    :data-test="getTestId('patientinput-sidebar')"
  >
    <MedButton
      icon="arrow_forward"
      rounded="full"
      size="sm"
      variant="primary"
      :class="
        cn('absolute top-2', {
          'right-1.5': hidden,
          '-right-4': !hidden,
        })
      "
      :icon-class="cn('transition-transform duration-200', {'rotate-180': !hidden})"
      :data-test="getTestId('patientinput-sidebar-toggle-hidden-button')"
      @click="toggleHidden()"
    />

    <h1
      class="text-xl font-semibold"
      :class="{
        '-ml-2 max-h-120 w-6 translate-x-2 rotate-180 truncate whitespace-nowrap [writing-mode:vertical-lr]': hidden,
      }"
    >
      Fragebogen bereitstellen
    </h1>

    <div v-if="!hidden">
      <div class="mt-2 space-y-2">
        <MedButton
          :data-test="getTestId('info-section-open-share-dialog-patient-button')"
          @click="openPatientInputDialog()"
        >
          Für Patienten
        </MedButton>

        <MedButton
          :data-test="getTestId('info-section-open-share-dialog-nurse-button')"
          @click="openCarerInputDialog()"
        >
          Für Pflegefachkräfte
        </MedButton>
      </div>

      <ComponentFoldable
        class="mt-4"
        title="Hilfe / Informationen"
        :test-id="getTestId('patientinput-client-sidebar-help')"
      >
        <p class="mt-2 text-sm">
          Durch den Patientenfragebogen bzw. den Fragebogen Pflege haben Sie die Möglichkeit, Daten für die
          Medikationsanalyse vorab von dem Patienten oder im Rahmen der Heimversorgung von der Pflegefachkraft
          (entspricht Arbeitshilfe Checkliste für Pflegefachkräfte nach BAK) digital erfassen zu lassen und davon
          ausgehend eine Medikationsanalyse zu starten.
        </p>
      </ComponentFoldable>
    </div>
  </aside>

  <PatientinputClientComponentShareDialog
    ref="refInfoDialog"
    :client-company="clientCompany"
    profile-edit-link:="profileEditLink"
    :test-id="getTestId('info-section')"
  />
</template>
